import { useState, useEffect, useRef } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Sparkles, Play, Pause, Clock, Shuffle, GripVertical, Loader2, Compass } from 'lucide-react'
import { tracks } from '../api/client'
import { usePlayerStore } from '../stores/player'
import LikeButton from '../components/LikeButton'
import TrackMenu from '../components/TrackMenu'
import type { TrackWithDetails } from '../types'
import { fetchAllTrackPages, shuffleTracks } from '../utils/trackQueue'

const PAGE_SIZE = 50

function formatDuration(ms: number | null): string {
  if (!ms) return '--:--'
  const seconds = Math.floor(ms / 1000)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatTotalDuration(totalMs: number): string {
  const totalMins = Math.floor(totalMs / 1000 / 60)
  if (totalMins < 60) {
    return `${totalMins} min`
  }
  const hours = Math.floor(totalMins / 60)
  const mins = totalMins % 60
  return `${hours} hr ${mins} min`
}

export default function UnplayedLiked() {
  const { playQueue, currentTrack, isPlaying } = usePlayerStore()
  const [orderedTracks, setOrderedTracks] = useState<TrackWithDetails[]>([])
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [isShuffleLoading, setIsShuffleLoading] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['tracks', 'liked-unplayed'],
    queryFn: ({ pageParam = 0 }) => tracks.likedUnplayed(PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined
      return allPages.length * PAGE_SIZE
    },
    initialPageParam: 0,
  })

  // Fetch total stats for header
  const { data: stats } = useQuery({
    queryKey: ['tracks', 'liked-unplayed', 'stats'],
    queryFn: () => tracks.likedUnplayedStats(),
  })

  // Flatten pages into single array
  const unplayedTracks = data?.pages.flat() ?? []

  // Sync orderedTracks with fetched data
  useEffect(() => {
    if (unplayedTracks.length > 0) {
      setOrderedTracks(unplayedTracks)
    }
  }, [data])

  // Set up scroll listener for infinite scroll
  useEffect(() => {
    const scrollContainer = document.getElementById('main-scroll-container')
    if (!scrollContainer) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      // Load more when within 200px of bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage()
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [fetchNextPage])

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index.toString())
    const target = e.target as HTMLElement
    setTimeout(() => {
      target.style.opacity = '0.5'
    }, 0)
  }

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.target as HTMLElement
    target.style.opacity = '1'
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragOverIndex(index)
    }
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null)
      return
    }

    const newTracks = [...orderedTracks]
    const [draggedTrack] = newTracks.splice(draggedIndex, 1)
    newTracks.splice(dropIndex, 0, draggedTrack)
    setOrderedTracks(newTracks)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  const handlePlayAll = () => {
    if (orderedTracks && orderedTracks.length > 0) {
      playQueue(orderedTracks as any, 0)
    }
  }

  const handleShufflePlay = async () => {
    setIsShuffleLoading(true)
    try {
      const allTracks = await fetchAllTrackPages(tracks.likedUnplayed, stats?.count)

      if (allTracks && allTracks.length > 0) {
        playQueue(shuffleTracks(allTracks) as any, 0)
      }
    } catch (error) {
      console.error('Failed to fetch all unplayed tracks for shuffle:', error)
      if (orderedTracks && orderedTracks.length > 0) {
        playQueue(shuffleTracks(orderedTracks) as any, 0)
      }
    } finally {
      setIsShuffleLoading(false)
    }
  }

  const handlePlayTrack = (index: number) => {
    if (orderedTracks) {
      playQueue(orderedTracks as any, index)
    }
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="w-40 h-40 sm:w-56 sm:h-56 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-xl">
          <Sparkles size={64} className="sm:w-20 sm:h-20 text-white" />
        </div>
        <div className="text-center sm:text-left">
          <p className="text-xs sm:text-sm text-dark-400 uppercase tracking-wider mb-1 sm:mb-2">Smart Playlist</p>
          <h1 className="text-2xl sm:text-4xl font-bold mb-2">Unplayed</h1>
          <p className="text-dark-400 text-sm mb-2">Songs you haven't listened to yet</p>
          <div className="flex items-center justify-center sm:justify-start gap-2 text-dark-300">
            <span>{stats?.count ?? orderedTracks?.length ?? 0} songs</span>
            {stats && stats.total_duration_ms > 0 && (
              <>
                <span className="text-dark-500">&bull;</span>
                <span>{formatTotalDuration(stats.total_duration_ms)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Play buttons */}
      {orderedTracks && orderedTracks.length > 0 && (
        <div className="mb-4 sm:mb-6 flex justify-center sm:justify-start gap-2">
          <button
            onClick={handlePlayAll}
            className="btn btn-primary flex items-center gap-2"
          >
            <Play size={18} />
            Play
          </button>
          <button
            onClick={handleShufflePlay}
            disabled={isShuffleLoading}
            className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            {isShuffleLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Shuffle size={18} />
            )}
            {isShuffleLoading ? 'Loading...' : 'Shuffle'}
          </button>
        </div>
      )}

      {/* Track list */}
      {orderedTracks && orderedTracks.length > 0 ? (
        <div className="card p-0 overflow-hidden">
          {/* Desktop table */}
          <table className="w-full hidden sm:table">
            <thead>
              <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                <th className="py-3 px-2 w-8"></th>
                <th className="py-3 px-4 w-12">#</th>
                <th className="py-3">Title</th>
                <th className="py-3">Artist</th>
                <th className="py-3 hidden md:table-cell">Album</th>
                <th className="py-3 px-4 w-20"></th>
                <th className="py-3 px-4 text-right">
                  <Clock size={16} />
                </th>
                <th className="py-3 px-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {orderedTracks.map((track: TrackWithDetails, index: number) => {
                const isCurrentTrack = currentTrack?.id === track.id
                const isDragOver = dragOverIndex === index
                return (
                  <tr
                    key={track.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`group hover:bg-dark-700/50 cursor-pointer transition-colors ${
                      isCurrentTrack ? 'bg-dark-700/30' : ''
                    } ${isDragOver ? 'border-t-2 border-primary-500' : ''}`}
                    onClick={() => handlePlayTrack(index)}
                  >
                    <td className="py-3 px-2">
                      <div
                        className="cursor-grab active:cursor-grabbing text-dark-500 hover:text-dark-300 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <GripVertical size={16} />
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {isCurrentTrack ? (
                        <span className="text-primary-400">
                          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                        </span>
                      ) : (
                        <>
                          <span className="text-dark-400 group-hover:hidden">{index + 1}</span>
                          <Play size={16} className="text-dark-100 hidden group-hover:block" />
                        </>
                      )}
                    </td>
                    <td className={`py-3 font-medium ${isCurrentTrack ? 'text-primary-400' : ''}`}>
                      {track.album_id ? (
                        <Link
                          to={`/albums/${track.album_id}`}
                          className="hover:text-primary-400 hover:underline transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {track.title}
                        </Link>
                      ) : (
                        track.title
                      )}
                    </td>
                    <td className={`py-3 ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {track.artist_id ? (
                        <Link
                          to={`/artists/${track.artist_id}`}
                          className="hover:text-primary-400 hover:underline transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {track.artist_name || 'Unknown'}
                        </Link>
                      ) : (
                        track.artist_name || 'Unknown'
                      )}
                    </td>
                    <td className={`py-3 hidden md:table-cell ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {track.album_id ? (
                        <Link
                          to={`/albums/${track.album_id}`}
                          className="hover:text-primary-400 hover:underline transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {track.album_title || 'Unknown'}
                        </Link>
                      ) : (
                        track.album_title || 'Unknown'
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <LikeButton trackId={track.id}  />
                    </td>
                    <td className={`py-3 px-4 text-right ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {formatDuration(track.duration_ms)}
                    </td>
                    <td className="py-3 px-2">
                      <TrackMenu track={track} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Mobile list */}
          <div className="sm:hidden divide-y divide-dark-700">
            {orderedTracks.map((track: TrackWithDetails, index: number) => {
              const isCurrentTrack = currentTrack?.id === track.id
              const isDragOver = dragOverIndex === index
              return (
                <div
                  key={track.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`flex items-center gap-2 p-3 hover:bg-dark-700/50 cursor-pointer ${
                    isCurrentTrack ? 'bg-dark-700/30' : ''
                  } ${isDragOver ? 'border-t-2 border-primary-500' : ''}`}
                  onClick={() => handlePlayTrack(index)}
                >
                  <div
                    className="cursor-grab active:cursor-grabbing text-dark-500 hover:text-dark-300 transition-colors touch-none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GripVertical size={16} />
                  </div>
                  <span className="w-6 text-sm text-center">
                    {isCurrentTrack ? (
                      <span className="text-primary-400">
                        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                      </span>
                    ) : (
                      <span className="text-dark-400">{index + 1}</span>
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    {track.album_id ? (
                      <Link
                        to={`/albums/${track.album_id}`}
                        className={`font-medium truncate block hover:underline ${isCurrentTrack ? 'text-primary-400' : 'hover:text-primary-400'}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {track.title}
                      </Link>
                    ) : (
                      <p className={`font-medium truncate ${isCurrentTrack ? 'text-primary-400' : ''}`}>
                        {track.title}
                      </p>
                    )}
                    <p className={`text-xs truncate ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {track.artist_id ? (
                        <Link
                          to={`/artists/${track.artist_id}`}
                          className="hover:text-primary-400 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {track.artist_name || 'Unknown'}
                        </Link>
                      ) : (
                        <span>{track.artist_name || 'Unknown'}</span>
                      )}
                      {' '}&bull; {formatDuration(track.duration_ms)}
                    </p>
                  </div>
                  <LikeButton trackId={track.id}  />
                  <TrackMenu track={track} />
                </div>
              )
            })}
          </div>

          {/* Load more trigger */}
          <div ref={loadMoreRef} className="py-4 flex justify-center">
            {isFetchingNextPage && (
              <Loader2 className="w-6 h-6 animate-spin text-dark-400" />
            )}
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <Sparkles size={48} className="mx-auto text-dark-500 mb-4" />
          <p className="text-dark-400 text-lg mb-2">No unplayed songs</p>
          <p className="text-dark-500 text-sm mb-6">
            Everything in your collection has been played
          </p>
          <Link
            to="/discover"
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Compass size={18} />
            Browse Catalog
          </Link>
        </div>
      )}
    </div>
  )
}
